import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { v4 as uuidv4 } from "uuid";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import L from "leaflet";
import Sidebar from "../components/SideBar";
import ReCAPTCHA from "react-google-recaptcha";



const Home = () => {
  const [activeTab, setActiveTab] = useState("rideInfo");
  const [isStep1Complete, setIsStep1Complete] = useState(false);
  const [isStep2Complete, setIsStep2Complete] = useState(false);
  const [childSeats, setChildSeats] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [pickup, setPickup] = useState({ name: "", lat: null, lng: null });
  const [dropoff, setDropoff] = useState({ name: "", lat: null, lng: null });
  const [searchResults, setSearchResults] = useState([]);
  const [activeField, setActiveField] = useState(""); // Tracks which field is active
  const [route, setRoute] = useState([]); // Store route coordinates
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const RECAPTCHA_SITE_KEY = "your_site_key_here";


  const [rideDetails, setRideDetails] = useState({
    serviceType: "",
    pickupDate: "",
    pickupTime: "",
    pickupLocation: "",
    dropoffLocation: "",
    passengers: 1,
    luggage: 0,
    vehicle: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
  });

  // Fetch vehicles from backend
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND}/api/vehicles`)
      .then((res) => res.json())
      .then((data) => setVehicles(data))
      .catch((err) => console.error("Error fetching vehicles:", err));
  }, []);


  useEffect(() => {
    if (pickup.lat && pickup.lng && dropoff.lat && dropoff.lng) {
      fetchRoute(pickup, dropoff);
    }
  }, [pickup, dropoff]); // Watch for changes in pickup and dropoff

  // Handle Form Changes
  const handleChange = (e) => {
    setRideDetails({ ...rideDetails, [e.target.name]: e.target.value });
  };


  // Debounce for handleSearch
  const searchTimeout = useRef();
  const handleSearch = (query, field) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    if (query.length > 2) {
      searchTimeout.current = setTimeout(async () => {
        let searchQuery = query;
        // If searching for dropoff and pickup is set in India, restrict to India
        if (field === "dropoff" && pickup && pickup.name && pickup.name.toLowerCase().includes("india")) {
          if (!query.toLowerCase().includes("india")) {
            searchQuery = `${query}, India`;
          }
        }
        const response = await axios.get(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`
        );
        setSearchResults(response.data);
        setActiveField(field);
      }, 350); // 350ms debounce
    } else {
      setSearchResults([]);
    }
  };
  const selectLocation = (place) => {
    const newLocation = {
      lat: parseFloat(place.lat),
      lng: parseFloat(place.lon),
      name: place.display_name,
    };

    if (activeField === "pickup") {
      setPickup(newLocation);
    } else if (activeField === "dropoff") {
      setDropoff(newLocation);
    }

    setSearchResults([]);
    setActiveField("");
  };


  const fetchRoute = async (pickup, dropoff) => {
    console.log("Fetching route between:", pickup, dropoff); // Debugging log
  
    const response = await fetch(
      `${process.env.REACT_APP_ROUTE_API_URL}/${pickup.lng},${pickup.lat};${dropoff.lng},${dropoff.lat}?overview=full&geometries=geojson`
    );
  
    const data = await response.json();
    console.log("Route API Response:", data); // Debugging log
  
    if (data.routes && data.routes.length > 0) {
      const newRoute = data.routes[0].geometry.coordinates.map(coord => ({
        lat: coord[1],
        lng: coord[0],
      }));
      console.log("Generated Route Coordinates:", newRoute); // Debugging log
      setRoute(newRoute);
    } else {
      console.error("No route found!");
    }
  };;


  // Validate Step 1
  const validateStep1 = () => {
    const { serviceType, pickupDate, pickupTime, pickupLocation, dropoffLocation, passengers, luggage } = rideDetails;
    console.log("pickupLocation:", pickupLocation);
    console.log("dropoffLocation:", dropoffLocation);
    if (serviceType && pickupDate && pickupTime && pickupLocation && dropoffLocation && passengers && luggage >= 0) {
      setIsStep1Complete(true);
      setActiveTab("selectVehicle");
    } else {
      alert("Please fill all required fields in Step 1.");
    }
  };

  // Validate Step 2
  const validateStep2 = () => {
    if (rideDetails.vehicle) {
      setIsStep2Complete(true);
      setActiveTab("finalDetails");
    } else {
      alert("Please select a vehicle before proceeding.");
    }
  };

  // Handle Form Submission (Step 3)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Include childSeats in rideDetails
    const rideData = { ...rideDetails, childSeats };

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND}/api/rides/book-ride`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(rideData),
      });

      const data = await response.json();
      alert(data.message);
    } catch (error) {
      alert("Error booking ride");
    }
  };

  // Add & Remove Child Seats
  const addChildSeat = () => {
    if (childSeats.length < 3) {
      setChildSeats([...childSeats, { id: uuidv4(), luggage: 0 }]);
    }
  };

  const removeChildSeat = (id) => {
    setChildSeats(childSeats.filter((seat) => seat.id !== id));
  };

  const updateLuggage = (id, change) => {
    setChildSeats((prevSeats) =>
      prevSeats.map((seat) =>
        seat.id === id ? { ...seat, luggage: Math.max(0, seat.luggage + change) } : seat
      )
    );
  };

  const MapUpdater = ({ pickup, dropoff }) => {
    const map = useMap();

    useEffect(() => {
      if (pickup.lat && pickup.lng && !dropoff.lat) {
        // Zoom in to Pick-Up location only
        map.setView([pickup.lat, pickup.lng], 14); // Adjust zoom level as needed
      } else if (pickup.lat && pickup.lng && dropoff.lat && dropoff.lng) {
        // Fit map to show both Pick-Up and Drop-Off locations
        const bounds = L.latLngBounds([pickup, dropoff]);
        map.fitBounds(bounds, { padding: [50, 50] }); // Add padding for better view
      }
    }, [pickup, dropoff, map]);

    return null;
  };


  return (
    <div className="home-page">
      <Sidebar />
      <div className="container mt-4">

        <form onSubmit={handleSubmit}>
          {/* Navigation Tabs */}
          <ul className="nav nav-pills nav-fill mb-3">
            <li className="nav-item">
              <button className={`nav-link ${activeTab === "rideInfo" ? "active" : ""}`} onClick={() => setActiveTab("rideInfo")}>
                Step 1: Ride Info
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${!isStep1Complete ? "disabled" : ""} ${activeTab === "selectVehicle" ? "active" : ""}`} onClick={() => isStep1Complete && setActiveTab("selectVehicle")}>
                Step 2: Select Vehicle
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${!isStep2Complete ? "disabled" : ""} ${activeTab === "finalDetails" ? "active" : ""}`} onClick={() => isStep2Complete && setActiveTab("finalDetails")}>
                Step 3: Final Details
              </button>
            </li>
          </ul>

          {/* Step 1 */}
          {activeTab === "rideInfo" && (
            <div>
             <h4 className="section-heading">Step 1: Ride Info</h4>
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">Select Service Type</label>
                  <select className="form-control" name="serviceType" onChange={handleChange} required>
                    <option value="">Select</option>
                    <option>From Airport</option>
                    <option>To Airport</option>
                    <option>Point to Point</option>
                    <option>Hourly/As Directed</option>
                  </select>

                  <label className="form-label mt-3">Pick-Up Date</label>
                  <input type="date" className="form-control" name="pickupDate" onChange={handleChange} required />

                  <label className="form-label mt-3">Pick-Up Time</label>
                  <input type="time" className="form-control" name="pickupTime" onChange={handleChange} required />

                  <label className="form-label mt-3">Pick-Up Location</label>

                  {/* Pick-Up Location Input Field */}

                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      placeholder="Enter Pick-Up Location..."
                      value={rideDetails.pickupLocation}
                      className="form-control"
                      name="pickupLocation"
                      onChange={(e) => {
                        setPickup({ ...pickup, name: e.target.value });
                        setRideDetails({ ...rideDetails, pickupLocation: e.target.value })
                        handleSearch(e.target.value, "pickup");
                      }}
                      onFocus={() => setActiveField("pickup")}
                    />
                    {/* Autocomplete Dropdown for Pick-Up */}
                    {searchResults.length > 0 && activeField === "pickup" && (
                      <ul
                        style={{
                          listStyle: "none",
                          padding: 0,
                          background: "#fff",
                          position: "absolute",
                          zIndex: 1000,
                          width: "100%",
                          border: "1px solid #ddd",
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        {searchResults.map((place) => (
                          <li
                            key={place.place_id}
                            onClick={() => selectLocation(place)}
                            style={{
                              padding: "10px",
                              cursor: "pointer",
                              borderBottom: "1px solid #ddd",
                            }}
                          >
                            {place.display_name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <label className="form-label mt-3">Drop-Off Location</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      placeholder="Enter Drop-Off Location..."
                      value={rideDetails.dropoffLocation}
                      className="form-control"
                      name="dropoffLocation"
                      onChange={(e) => {
                        setDropoff({ ...dropoff, name: e.target.value });
                        setRideDetails({ ...rideDetails, dropoffLocation: e.target.value })
                        handleSearch(e.target.value, "dropoff");
                      }}
                      onFocus={() => setActiveField("dropoff")}
                    />
                    {/* Autocomplete Dropdown for Drop-Off */}
                    {searchResults.length > 0 && activeField === "dropoff" && (
                      <ul
                        style={{
                          listStyle: "none",
                          padding: 0,
                          background: "#fff",
                          position: "absolute",
                          zIndex: 1000,
                          width: "100%",
                          border: "1px solid #ddd",
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        {searchResults.map((place) => (
                          <li
                            key={place.place_id}
                            onClick={() => selectLocation(place)}
                            style={{
                              padding: "10px",
                              cursor: "pointer",
                              borderBottom: "1px solid #ddd",
                            }}
                          >
                            {place.display_name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="d-flex gap-3 mt-3">
                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mt-3">Passengers</label>
                      <input type="number" className="form-control" name="passengers" placeholder="passengers" onChange={handleChange} min="1" />

                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mt-3">Luggage</label>
                      <input type="number" className="form-control" name="luggage" placeholder="luggage" onChange={handleChange} min="0" />

                    </div>


                  </div>
                  <button type="button" className="btn btn-link text-decoration-none text-dark" onClick={addChildSeat} disabled={childSeats.length >= 3}>
                    + Add Child Seat
                  </button>

                  {childSeats.map((seat) => (
                    <div key={seat.id} className="input-group mb-2 mt-2">
                      <select
                        className="form-control"
                        value={seat.type || ""}
                        onChange={(e) =>
                          setChildSeats((prevSeats) =>
                            prevSeats.map((s) =>
                              s.id === seat.id ? { ...s, type: e.target.value } : s
                            )
                          )
                        }
                      >
                        <option value="">Select Seat Type</option>
                        <option value="Rear Facing Seat Infant">Rear Facing Seat Infant</option>
                        <option value="Forward Facing Seat Toddler">Forward Facing Seat Toddler</option>
                        <option value="Booster Seat">Booster Seat</option>
                      </select>

                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => updateLuggage(seat.id, -1)}
                        >
                          -
                        </button>
                        <span>{seat.luggage}</span>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => updateLuggage(seat.id, 1)}
                        >
                          +
                        </button>
                      </div>

                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => removeChildSeat(seat.id)}
                      >
                        Delete
                      </button>
                    </div>
                  ))}

                  <button className="btn btn-dark w-100 mt-3" type="button" onClick={validateStep1}>
                    Next: Select Vehicle
                  </button>
                </div>
                <div className="col-md-6 ">
                // Update TileLayer URL
<MapContainer center={[10, 78]} zoom={8} style={{ height: "400px", width: "100%" }}>
  <TileLayer
    url={process.env.REACT_APP_TILE_LAYER_URL}
    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  />
  {/* Dynamic Map Updates */}
  <MapUpdater pickup={pickup} dropoff={dropoff} />

  {/* Pick-Up Marker */}
  {pickup.lat && pickup.lng && (
    <Marker position={[pickup.lat, pickup.lng]} icon={L.icon({ iconUrl: "https://leafletjs.com/examples/custom-icons/leaf-green.png", iconSize: [25, 41] })} />
  )}

  {/* Drop-Off Marker */}
  {dropoff.lat && dropoff.lng && (
    <Marker position={[dropoff.lat, dropoff.lng]} icon={L.icon({ iconUrl: "https://leafletjs.com/examples/custom-icons/leaf-red.png", iconSize: [25, 41] })} />
  )}

  {/* Route Line */}
  {route.length > 0 && <Polyline positions={route} color="blue" weight={5} />}
</MapContainer>




                </div>
              </div>
            </div>
          )}
          <div>

          {/* Step 2 */}
            {activeTab === "selectVehicle" && (
              <div>
               <h4 className="section-heading">Step 2: Select Vehicle</h4>
               {/* Vehicle Selection Cards */}
                {vehicles.length > 0 ? (
                  <div className="row">
                    {vehicles.map((vehicle) => (
                      <div key={vehicle._id} className="col-md-4 mb-2"> {/* Adjust col size as needed */}
                        <div className="card">
                          <img
                            src={`${process.env.REACT_APP_BACKEND}${vehicle.image}`}
                            alt={vehicle.model}
                            className="card-img-top"
                            style={{ width: "100%", height: "150px", objectFit: "cover" }} // Adjust image styling
                          />
                          <div className="card-body">
                            <h5>{vehicle.type}</h5>
                            <h5>{vehicle.brand} - {vehicle.model}</h5>
                            <h5>mileage {vehicle.mileage} - Rent Price {vehicle.rent}</h5>

                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={() =>
                                setRideDetails({
                                  ...rideDetails,
                                  vehicle: vehicle.model,
                                  rent: vehicle.rent, // Store rent price
                                })
                              }
                            >
                              Select
                            </button>

                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No vehicles available</p>
                )}
                <button className="btn btn-dark w-100 mt-3" type="button" onClick={validateStep2}>
                  Next: Final Details
                </button>
              </div>

            )}
          </div>

          {/* Step 3 */}
          {activeTab === "finalDetails" && (
            <div>
               <h4 className="section-heading">Step 3: Final Details</h4>
               {/* Final Details Form */}
              <div className="row">
                <div className="col-md-6 border-end p-4">
                  <h4 className="mb-4 text-primary">Personal Information</h4>

                  <div className="mb-3">
                    <label className="form-label fw-bold">First Name</label>
                    <input type="text" className="form-control" name="firstName" onChange={handleChange} required />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Last Name</label>
                    <input type="text" className="form-control" name="lastName" onChange={handleChange} required />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Phone Number</label>
                    <input type="tel" className="form-control" name="phoneNumber" onChange={handleChange} required />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Email Address</label>
                    <input type="email" className="form-control" name="email" onChange={handleChange} required />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card border-dark">
                    <div className="card-header text-center bg-dark text-white">
                      <h4>Ride Receipt</h4>
                    </div>
                    <div className="card-body">
                      <table className="table table-bordered">
                        <tbody>
                          <tr>
                            <td><b>Service Type</b></td>
                            <td>{rideDetails.serviceType}</td>
                          </tr>
                          <tr>
                            <td><b>Pickup Date</b></td>
                            <td>{rideDetails.pickupDate}</td>
                          </tr>
                          <tr>
                            <td><b>Pickup Time</b></td>
                            <td>{rideDetails.pickupTime}</td>
                          </tr>
                          <tr>
                            <td><b>Pickup Location</b></td>
                            <td>{rideDetails.pickupLocation}</td>
                          </tr>
                          <tr>
                            <td><b>Dropoff Location</b></td>
                            <td>{rideDetails.dropoffLocation}</td>
                          </tr>
                          <tr>
                            <td><b>Passengers</b></td>
                            <td>{rideDetails.passengers}</td>
                          </tr>
                          <tr>
                            <td><b>Luggage</b></td>
                            <td>{rideDetails.luggage}</td>
                          </tr>
                            {/* Show Child Seats if any */}
                            {childSeats.length > 0 && (
                              <tr>
                                <td><b>Child Seats</b></td>
                                <td>
                                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {childSeats.map((seat, idx) => (
                                      <li key={seat.id}>
                                        {seat.type ? seat.type : "Child Seat"}
                                        {typeof seat.luggage === "number" && ` (Luggage: ${seat.luggage})`}
                                      </li>
                                    ))}
                                  </ul>
                                </td>
                              </tr>
                            )}
                          <tr>

                            <td><b>Selected Vehicle</b></td>
                            <td>{rideDetails.vehicle}</td>

                          </tr>
                          <tr>
                            <td><b>Rent Price</b></td>
                            <td>${rideDetails.rent}</td> {/* Display Rent Price */}
                          </tr>




                        </tbody>
                      </table>
                    </div>
                    <div className="card-footer text-center">
                      <small>Thank you for choosing our service!</small>
                    </div>
                  </div>
                </div>
              </div>
              {/*             <ReCAPTCHA
  sitekey={RECAPTCHA_SITE_KEY}
  onChange={() => setCaptchaVerified(true)}
  onExpired={() => setCaptchaVerified(false)}
/>
 */}
              <button
                className="btn btn-success w-100 mt-3"
                type="submit"
              //disabled={!captchaVerified}
              >
                Confirm & Book Ride
              </button>

            </div>
          )}
        </form>
      </div>
    </div>

  );
};

export default Home;
